import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

const RELOAD_EVENT = "media:list-changed"

export default class extends Controller {
  static targets = ["modal", "count", "submitButton", "successToast", "errorToast"]
  static values = { url: String }

  connect() {
    this.pendingMediaIds = []
    this.boundToggleSubmit = () => this.toggleSubmit()
    if (this.hasModalTarget) {
      this.modalTarget.addEventListener("app-select:change", this.boundToggleSubmit)
    }
    this.toggleSubmit()
  }

  disconnect() {
    if (this.hasModalTarget && this.boundToggleSubmit) {
      this.modalTarget.removeEventListener("app-select:change", this.boundToggleSubmit)
    }
  }

  open(event) {
    if (event) event.preventDefault()

    this.pendingMediaIds = this.mediaIdsFrom(event)
    if (this.pendingMediaIds.length === 0) return

    if (this.hasCountTarget) {
      const noun = this.pendingMediaIds.length === 1 ? "item" : "items"
      this.countTarget.textContent = `${this.pendingMediaIds.length} ${noun}`
    }

    this.toggleSubmit()
    Modal.getOrCreateInstance(this.modalTarget).show()
  }

  async submit(event) {
    if (event) event.preventDefault()

    const mediaIds = this.pendingMediaIds || []
    const companyIds = this.selectedCompanyIds()
    const roles = this.selectedRoles()
    if (mediaIds.length === 0 || companyIds.length === 0 || roles.length === 0) return

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({ media_ids: mediaIds, company_ids: companyIds, roles })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        return this.showSnackbar("error", Array(payload.errors).flat().filter(Boolean)[0] || "Couldn't assign the selected media")
      }

      const payload = await response.json().catch(() => ({}))
      Modal.getOrCreateInstance(this.modalTarget).hide()
      this.reloadAfterAssign()
      this.showSnackbar("success", this.summary(payload.company_count || companyIds.length))
    } catch {
      this.showSnackbar("error", "Couldn't assign the selected media")
    }
  }

  reloadAfterAssign() {
    const hubFrame = this.element.closest("turbo-frame#media_hub_frame")
    if (hubFrame && typeof hubFrame.reload === "function") {
      hubFrame.reload()
      return
    }

    window.dispatchEvent(new CustomEvent(RELOAD_EVENT))
  }

  mediaIdsFrom(event) {
    const paramIds = event?.params?.mediaIds
    if (paramIds != null && paramIds !== "") {
      return [paramIds].flat().map(String)
    }

    return [...this.element.querySelectorAll('[data-bulk-select-target="checkbox"]:checked')]
      .filter(checkbox => !checkbox.closest("tr[hidden]"))
      .map(checkbox => checkbox.value)
  }

  selectedCompanyIds() {
    return this.selectedValues("company_ids[]")
  }

  selectedRoles() {
    return this.selectedValues("roles[]")
  }

  selectedValues(name) {
    const selectedOptions = [...this.element.querySelectorAll(`select[name="${name}"] option:checked`)]
      .map(option => option.value)
      .filter(value => value !== "")
    if (selectedOptions.length > 0) return selectedOptions

    return [...this.element.querySelectorAll(`input[name="${name}"]`)]
      .map(input => input.value)
      .filter(value => value !== "")
  }

  // Both an organization and a role are required (TMS-1602).
  toggleSubmit() {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = this.selectedCompanyIds().length === 0 || this.selectedRoles().length === 0
  }

  summary(companyCount) {
    const noun = companyCount === 1 ? "organization" : "organizations"
    return `Assigned to ${companyCount} ${noun}`
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  showSnackbar(type, title) {
    const template = type === "error"
      ? (this.hasErrorToastTarget ? this.errorToastTarget : null)
      : (this.hasSuccessToastTarget ? this.successToastTarget : null)
    if (!template) return

    const node = template.content.firstElementChild.cloneNode(true)
    const titleEl = node.querySelector(".snackbar-title span")
    if (titleEl) titleEl.textContent = title

    this.flashContainer().appendChild(node)
  }

  flashContainer() {
    let container = document.querySelector(".app-flash-alert-container")
    if (!container) {
      container = document.createElement("div")
      container.className = "app-flash-alert-container"
      document.body.appendChild(container)
    }
    return container
  }
}
